import type { IntegrationItem } from '../types'

export default {
  nav: {
    features: "Features",
    pricing: "Pricing",
    integrations: "Integrations",
    how: "How it works",
    cta: "Get in touch",
    download: "Download",
    apps: "Apps",
    menu: "Menu",
    primary_nav: "Primary navigation",
  },
  hero: {
    title_1: "One menu, one stock count —",
    title_2: "the whole venue",
    title_3: "in one system",
    subtitle:
      "Your POS, your own delivery storefront, QR menu, waiter app, screens and warehouse all run on one menu. Change a price once and it changes everywhere. Sell the last portion and it disappears from your storefront the same second.",
    cta_primary: "Get in touch",
    cta_secondary: "How it works",
    trust: "First month free, full set · Our team does the setup",
    stat_venues_val: "20+",
    stat_venues: "venues",
    stat_country: "Uzbekistan",
    stat_payments: "Click / Payme / Rahmat",
    stat_trial_val: "1 month",
    stat_trial: "free",
    dashboard: {
      url: "samly.uz/dashboard",
      home: "Home",
      orders: "Orders",
      menu: "Menu",
      kitchen: "Kitchen",
      analytics: "Analytics",
      staff: "Staff",
      revenue_label: "Revenue today",
      currency: "UZS",
      orders_count: "Orders",
      avg_check: "Avg. check",
      avg_time: "Avg. time",
      avg_time_val: "14 min",
      paid: "Paid",
      waiting: "Waiting",
      order: "Order",
      table: "Table",
    },
  },
  logos: {
    title: "Works in venues of every format",
    formats: {
      restaurants: "Restaurants",
      cafes: "Coffee shops",
      oshkhona: "Oshxona",
      fastfood: "Fast food",
      chains: "Chains",
    },
  },
  painpoints: {
    title: "Three systems — and all three count differently",
    subtitle:
      "Your POS, a stock sheet in Excel and a storefront on someone else's app know nothing about each other. You find out from a guest.",
    before_label: "Without Samly",
    after_label: "With Samly",
    before: [
      "You raise a price at the POS — the delivery storefront still shows the old one",
      "The plov ran out, but orders for it keep coming in",
      "Stock in Excel, POS on its own, delivery on an aggregator",
      "A waiter promises a guest a dish the kitchen doesn't have",
      "Building one report means reconciling three exports by hand",
    ],
    after: [
      "Change a price once — it changes across every sales point",
      "Sell the last portion and the dish vanishes from the storefront",
      "Warehouse, POS and delivery count the same stock",
      "The stop list reaches the waiter, the kitchen and the guest at once",
      "One report for the whole venue — nothing to reconcile",
    ],
  },
  onemenu: {
    tag: "How it works",
    title: "One menu. One stock count. Six sales points.",
    subtitle: "You keep the menu and the stock in one place. Samly keeps everything else current.",
    hub_title: "One menu",
    hub_sub: "one stock count",
    surfaces: {
      pos: "POS",
      storefront: "Own delivery",
      qr: "QR menu",
      waiter: "Waiter app",
      screens: "Screens",
      warehouse: "Warehouse",
    },
    moments: [
      {
        title: "Change a price once",
        desc: "The new price is live at the POS, in the QR menu, on your delivery storefront, in the waiter app and on the screens. Nothing to duplicate.",
      },
      {
        title: "Sell the last portion",
        desc: "The dish hits the stop list that same second: it disappears from the storefront, the guest stops seeing it, and the waiter can no longer ring it up.",
      },
      {
        title: "Stock counts ingredients",
        desc: "Every dish has a recipe. Sell a plov and the rice, oil and meat leave the warehouse. Your stock figure is real, not an estimate.",
      },
    ],
  },
  stats: {
    items: [
      { val: "20+", label: "venues", sub: "running on Samly" },
      { val: "1 month", label: "free", sub: "full set, no card" },
      { val: "9", label: "components", sub: "pay only for yours" },
      { val: "−30%", label: "paying yearly", sub: "3-month minimum term" },
    ],
  },
  features: {
    title: "Six sales points, one menu underneath",
    subtitle: "Switch on what your venue needs. You simply don't buy the rest.",
    tabs: {
      pos: "POS",
      storefront: "Own delivery",
      qr: "QR menu",
      waiter: "Waiter app",
      kitchen: "Kitchen",
      warehouse: "Warehouse",
    },
    mock: {
      hall: "Floor · 12 tables",
      st_free: "free",
      st_paid: "paid",
      st_waiting: "waiting",
      st_ordered: "ordered",
      accept_payment: "Take payment",
      pin: "PIN",
      my_tables: "My tables",
      online: "online",
      guests: "guests",
      positions: "items",
      cooking: "Cooking",
      ready: "Ready",
      accepted: "Accepted",
      kitchen_station: "Kitchen · hot station",
      delivery: "Delivery",
      storefront_head: "Your storefront · Telegram",
      add_to_cart: "Add to cart",
      sold_out: "Sold out",
      checkout: "Check out",
      warehouse_head: "Deducted by recipe",
      warehouse_sold: "1 plov sold",
      warehouse_left: "left",
      stop_list: "Stop list",
      table: "Table",
      total: "Total",
      currency: "UZS",
      your_order: "Your order",
      pay_click: "Pay with Click",
      pay_payme: "Pay with Payme",
      dishes: {
        plov: "Plov",
        lagman: "Lagman",
        tea: "Tea",
        shashlik: "Shashlik",
        manti: "Manti",
        soup: "Soup",
        salad: "Salad",
      },
      units: { g: "g", kg: "kg", ml: "ml", l: "l" },
      ingredients: {
        rice: "Rice",
        oil: "Oil",
        meat: "Beef",
        carrot: "Carrot",
      },
    },
    pos: {
      title: "A POS that knows the real stock",
      points: [
        "Cash, card and QR in one window",
        "Refunds, discounts and deletions need a manager PIN",
        "Shifts and cash discrepancies stay under control",
        "E-Kassa (OFD) — fiscal reporting the way Uzbekistan requires",
        "Works offline and syncs afterwards",
      ],
    },
    storefront: {
      title: "Your own storefront, not a shelf on someone else's app",
      points: [
        "Telegram Mini App, website and mobile app",
        "Your own domain — guests see your brand, not a marketplace",
        "Payment via Click, Payme and Rahmat",
        "Orders land straight in the POS and the kitchen",
        "No aggregator commission on your own orders",
      ],
    },
    qr: {
      title: "Guests order from their own phone",
      points: [
        "A QR code on the table opens the menu in a browser — no app",
        "One shared cart for the whole table",
        "Payment via Click, Payme and Rahmat",
        "The same menu and the same stock as the POS",
        "Guests track order status instead of asking",
      ],
    },
    waiter: {
      title: "A waiter can't sell what you don't have",
      points: [
        "A tablet or any Android phone",
        "The stop list updates in real time",
        "Notes per dish: no onion, extra spicy",
        "Add to a table's order without rebuilding it",
        "Push notification when a dish is ready",
      ],
    },
    kitchen: {
      title: "The cook sees the order, not a paper ticket",
      points: [
        "Automatic routing by station: hot, cold, bar",
        "A timer per dish, taken from its recipe card",
        "Statuses: in progress → done → waiter notified",
        "Prep-time analytics for every cook",
        "Included in your subscription at no extra cost",
      ],
    },
    warehouse: {
      title: "Stock counts ingredients, not portions",
      points: [
        "A recipe card for every dish",
        "Sell a plov and the rice, oil and meat are deducted",
        "Cost and margin per menu item",
        "The stop list builds itself the moment an ingredient runs out",
        "Several warehouses, if you run more than one kitchen",
      ],
    },
  },
  integrations: {
    title: "Works with what you already use",
    subtitle: "Payments, delivery, hardware and fiscal reporting — out of the box.",
    soon: "on the roadmap",
    groups: {
      payment: {
        title: "Payments",
        items: [
          { name: "Click", desc: "QR and online payment" },
          { name: "Payme", desc: "Online payment" },
          { name: "Rahmat", desc: "Online payment" },
          { name: "Uzum Pay", desc: "Planned", soon: true },
        ] as IntegrationItem[],
      },
      delivery: {
        title: "Delivery",
        items: [
          { name: "Own storefront", desc: "Telegram, web, mobile app" },
          { name: "Yandex Eats", desc: "Orders arrive automatically" },
          { name: "Uzum Tezkor", desc: "Planned", soon: true },
        ] as IntegrationItem[],
      },
      equipment: {
        title: "Hardware",
        items: [
          { name: "ESC/POS", desc: "Any receipt printer" },
          { name: "StarPRNT", desc: "Automatic receipt printing" },
          { name: "Smart TV / Fire Stick", desc: "For menu screens" },
        ] as IntegrationItem[],
      },
      local: {
        title: "Uzbekistan",
        items: [
          { name: "E-Kassa (OFD)", desc: "Fiscal reporting" },
          { name: "Android / iOS", desc: "Any device" },
          { name: "Open API", desc: "Your own integrations, included" },
        ] as IntegrationItem[],
      },
    },
  },
  dashboardPreview: {
    owner: {
      badge: "Owner",
      title: "Every branch on one screen",
      features: {
        revenue: "Real-time revenue across all branches",
        alert: "An alert the moment a shift closes with a discrepancy",
        reports: "An automatic report for every single shift",
      },
      cta: "See the features",
      mockup: {
        title: "3 branches · today",
        live: "live",
        revenue: "UZS",
        week: "vs last week",
        branches: {
          chilanzar: "Chilanzar",
          yunusabad: "Yunusabad",
          samarkand: "Samarkand",
        },
      },
    },
    qr: {
      badge: "QR menu",
      title: "Guests order and pay on their own",
      features: {
        no_app: "No app needed — just point the camera",
        payments: "Click, Payme and Rahmat in under a minute",
        cart: "One shared cart for the whole table",
      },
      cta: "How the QR menu works",
      slides: {
        menu: "Menu and availability",
        dish: "Dish card",
        cart: "Cart and payment",
      },
    },
    monitors: {
      badge: "Screens",
      title: "Screens show the very same menu",
      features: {
        playlist: "Video and photo on a schedule",
        offline: "Runs without internet — from a USB drive",
        update: "Change the content from your phone in seconds",
      },
      cta: "More about screens",
      mockup: {
        menu_day: "Menu of the day",
        lunch: "Business lunch",
        action: "PROMO −15%",
        items: {
          lagman: "Lagman",
          plov: "Plov",
          shashlik: "Shashlik",
          manti: "Manti",
        },
      },
    },
  },
  components: {
    title: "Pay only for what you use",
    subtitle: "There are no plans. The price is the sum of your components. Everything else is included.",
    paid_title: "Subscription components",
    paid_note:
      "Prices are monthly. Own delivery, the waiter app, the QR menu and Yandex Eats are bought per branch.",
    free_title: "Included — at no extra cost",
    free_badge: "Included",
    per_month: "/mo",
    per_branch: "per branch",
    min_one: "minimum 1",
    cta: "Build your set",
    items: {
      branches: { name: "Branches", desc: "Each location with its own floor and stock" },
      employees: { name: "Employees", desc: "One account per person, the owner included" },
      pos_devices: { name: "POS devices", desc: "A register on a monoblock, tablet or phone" },
      online_orders: {
        name: "Own delivery",
        desc: "Telegram Mini App, website and app on your own domain",
      },
      waiter_app: {
        name: "Waiter app",
        desc: "Orders from a tablet or phone, with a live stop list",
      },
      qr_menu: { name: "QR menu", desc: "Menu and payment from the guest's phone" },
      yandex: { name: "Yandex Eats", desc: "Yandex Eats orders arrive automatically" },
      monitors: { name: "Menu screens", desc: "Digital menu and promos on any Smart TV" },
      warehouses: { name: "Warehouses", desc: "Ingredient tracking driven by recipe cards" },
    },
    free_items: {
      kds: { name: "Kitchen (KDS)", desc: "A cook's screen with timers and a queue" },
      manager_app: { name: "Manager app", desc: "Floor plan and staff from your phone" },
      loyalty_crm: { name: "Loyalty and CRM", desc: "Guest database and loyalty program" },
      ai_analytics: { name: "AI analytics", desc: "Forecasts and sales suggestions" },
      kiosk: { name: "Self-service kiosk", desc: "Guests place their own orders" },
      open_api: { name: "Open API", desc: "Integrations with your own systems" },
      stop_list: {
        name: "Real-time stop list",
        desc: "Out of stock means hidden everywhere, at once",
      },
      cash_shift: { name: "Shifts and Z-reports", desc: "Opening, closing, discrepancies" },
      order_management: { name: "Order management", desc: "Create, change, cancel" },
      basic_analytics: { name: "Analytics", desc: "Revenue, average check, top dishes" },
      order_status: { name: "Order status screen", desc: "For guests in the dining room" },
      e_kassa: { name: "E-Kassa (OFD)", desc: "Fiscal reporting for Uzbekistan" },
      pin_protection: { name: "PIN-protected refunds", desc: "A refund needs manager approval" },
      floor_plan: { name: "Floor plan and tables", desc: "A visual layout of the room" },
      owner_push: { name: "Owner push alerts", desc: "Notifications about violations" },
      qr_generator: { name: "QR code generator", desc: "For tables — print and export" },
      payment_types: { name: "Payment types", desc: "Cash, card, QR" },
      receipt_printer: { name: "Receipt printing", desc: "ESC/POS, StarPRNT — any hardware" },
    },
  },
  pricing: {
    tag: "Pricing",
    title: "No plans. Build your own set.",
    subtitle: "The price is the sum of your components, pegged to BRV. Minimum term is 3 months.",
    from_label: "from",
    per_month: "UZS/mo",
    from_note: "for the minimum set on a 12-month term",
    term_title: "Billing term",
    term_note: "3 months minimum",
    build_title: "Build your set",
    reset: "Reset",
    col_component: "Component",
    col_unit: "Unit price",
    col_qty: "Qty",
    col_sum: "Total/mo",
    brv_label: "BRV, UZS",
    brv_unit: "BRV",
    brv_note:
      "1 BRV = {{value}} UZS. Prices are pegged to BRV: when it changes, the amount in UZS is recalculated automatically.",
    matrix_title: "Your set by term",
    col_term: "Term",
    col_discount: "Discount",
    col_per_month: "Per month",
    col_total: "Total",
    receipt_title: "QUOTE",
    receipt_empty: "Nothing selected",
    receipt_base: "Monthly price before discount",
    receipt_full: "{{months}} months before discount",
    receipt_discount: "Term discount",
    receipt_total_sub: "UZS for {{months}} months",
    receipt_per_month: "Monthly price after discount",
    receipt_save: "You save",
    show_detail: "Show the maths",
    hide_detail: "Hide the maths",
    month_one: "month",
    month_few: "months",
    month_many: "months",
    rules_title: "How it adds up",
    rules: [
      "The price is the sum of your components. There are no plans.",
      "The minimum purchase term is 3 months.",
      "Term discount: 3 months −10%, 6 months −20%, 12 months −30%.",
      "The total is rounded down to 0.05 BRV.",
      "Minimum: 1 branch, 1 employee, 1 POS device.",
      "Own delivery, the waiter app, the QR menu and Yandex Eats are per branch and can never exceed your branch count.",
    ],
    notes_title: "Billed separately",
    notes: [
      "Onboarding and setup are free, handled by our team.",
      "Hardware (monoblock, printer, screens) is bought separately. It's a one-off cost and the amount varies a lot — we'll help you spec it for your room and budget.",
    ],
    cta: "Get in touch",
    cta_note: "First month free with the full set. After that, a 3-month minimum term.",
  },
  faq: {
    tag: "Questions",
    title: "Frequently asked questions",
    items: [
      {
        q: "How is the price calculated?",
        a: "There are no plans. You assemble a set from nine components and the price is their sum. Prices are pegged to BRV, the minimum purchase term is 3 months, and the term discount is 3 months −10%, 6 months −20%, 12 months −30%. The minimum set is 1 branch, 1 employee and 1 POS device.",
      },
      {
        q: "What is included at no extra cost?",
        a: "Kitchen (KDS), the manager app, loyalty and CRM, AI analytics, the self-service kiosk, Open API, the stop list, shifts and Z-reports, the floor plan, E-Kassa (OFD), PIN-protected refunds, owner push alerts and receipt printing. You only pay for branches, employees, POS devices, own delivery, the waiter app, the QR menu, Yandex Eats, screens and warehouses.",
      },
      {
        q: "What is BRV, and why price in it?",
        a: "BRV is Uzbekistan's basic calculation unit, which many official amounts are pegged to. Right now 1 BRV = 412,000 UZS. Pricing in BRV means we never rewrite the price list by hand: when BRV changes, the amount in UZS is recalculated automatically. You still pay in UZS.",
      },
      {
        q: "Does the warehouse really count ingredients rather than portions?",
        a: "Yes. Every dish has a recipe card. When you sell a plov, the rice, oil and meat are deducted from stock in the quantities you specified. That's why the stock figure is real, the cost is calculated for you, and the stop list builds itself the moment any ingredient runs out.",
      },
      {
        q: "How does own delivery work?",
        a: "You get your own storefront: a Telegram Mini App, a website and a mobile app, on your own domain if you want one. Payment is Click, Payme and Rahmat. Orders land straight in the POS and the kitchen, on the same menu and the same stock. You pay no aggregator commission on those orders. Yandex Eats can be connected as well if you need it.",
      },
      {
        q: "How long does rollout take?",
        a: "Up to a week. Our team does the setup: we migrate the menu, build the recipe cards, connect the hardware and train your staff. Onboarding is free.",
      },
      {
        q: "Do I need to buy hardware?",
        a: "If you don't already have it, yes. A monoblock for the register, a receipt printer and screens for the kitchen and menu are bought separately. It's a one-off cost and the amounts vary a great deal, which is why it isn't in the subscription. We'll tell you exactly what your room needs and spec it to your budget. Samly runs on Android and iOS, so an ordinary tablet covers part of it.",
      },
      {
        q: "Does it work without internet?",
        a: "The POS and the kitchen screen work offline. Menu screens can play content from a USB drive. When the connection returns, data syncs by itself — no order is lost.",
      },
      {
        q: "Which integrations are live today?",
        a: "Payments: Click, Payme and Rahmat. Delivery: your own storefront and Yandex Eats. Fiscal: E-Kassa (OFD). Hardware: any ESC/POS or StarPRNT printer. Uzum Pay and Uzum Tezkor are on our roadmap.",
      },
      {
        q: "What does the free month include?",
        a: "The full set of components for a month, with no card and no commitment. If you stay on after that, the 3-month minimum term applies.",
      },
      {
        q: "Is there support in Uzbek?",
        a: "Yes. The interface is available in Uzbek, Russian and English. Support answers in Uzbek and Russian by phone and on Telegram, daily except Sunday, 10:00–22:00.",
      },
    ],
  },
  cta: {
    title: "Let us show you Samly on your own menu",
    subtitle: "First month free with the full set · Our team does the setup",
    note: "Onboarding is free. Hardware is bought separately — we'll help you spec it.",
    cta_primary: "Get in touch",
    cta_secondary: "Call us",
    trust_payment: "Click · Payme · Rahmat",
    trust_support: "Support 6 days a week",
    trust_local: "Built for Uzbekistan",
  },
  footer: {
    description:
      "One menu and one stock count for the whole venue: POS, own delivery, QR menu, waiter app, screens and warehouse.",
    product: "Product",
    support: "Support",
    contacts: "Contacts",
    copyright: "All rights reserved.",
    lang_label: "Language",
    links: {
      features: "Features",
      pricing: "Pricing",
      integrations: "Integrations",
      modules: "What's included",
      apps: "Apps",
      faq: "FAQ",
      contact: "Get in touch",
      telegram: "Telegram",
    },
  },
  contact: {
    title: "Let's talk about your venue",
    subtitle: "Call or message us — we'll show you Samly and set it up with you.",
    call: "Call us",
    telegram_label: "Telegram",
    instagram_label: "Instagram",
    hours_label: "Working hours",
    hours: "Mon–Sat, 10:00–22:00",
    trust: "First month free · Our team does the setup",
  },
  modal: {
    title: "Try Samly for free",
    subtitle: "Leave a request — a manager will get back to you during working hours",
    name_placeholder: "Your name",
    phone_placeholder: "Phone number",
    submit: "Send request",
    submitting: "Sending...",
    success_title: "Request received!",
    success_desc: "A manager will get back to you during working hours",
    error_required: "Required field",
    error_name_min: "Minimum 2 characters",
    error_phone: "Enter a valid phone number",
    error_server: "Error. Please try again",
    toast_error_title: "Failed to send",
  },
  apps: {
    title: "Our apps",
    subtitle: "The POS goes on your computer; the owner and waiter apps go on your phone.",
    pos: {
      title: "Samly POS for Windows",
      desc: "The checkout app for your counter computer: orders, payments, receipt printing.",
      button: "Download for Windows",
      mobileHint: "Install it on your POS computer",
    },
    owner: { title: "Owner app", desc: "Revenue, shifts and reports for all your venues on your phone." },
    waiter: { title: "Waiter app", desc: "Take orders at the table and send them to the kitchen." },
    soon: "Coming soon",
    downloadOn: "Download on the",
    getItOn: "Get it on",
  },
  installGuide: {
    title: "How to install the POS",
    subtitle: "Your download has started. If Windows shows a warning, that is expected — follow the steps below.",
    subtitleHash: "Download the installer with the button below and follow the steps. If Windows shows a warning, that is expected.",
    steps: {
      browser: {
        title: "Keep the file",
        text: "Your browser may say the file is not commonly downloaded. Click “Keep”. In Edge: “…” → “Keep” → “Show more” → “Keep anyway”.",
      },
      smartscreen: {
        title: "Open the installer",
        text: "Run the downloaded file. Windows shows “Windows protected your PC”. Click “More info”.",
      },
      runAnyway: {
        title: "Allow it to run",
        text: "A “Run anyway” button appears. Click it. This window appears because the installer does not have a digital signature yet.",
      },
      installer: {
        title: "Install the POS",
        text: "Windows asks whether the app may make changes — click “Yes”. Complete the setup wizard. A Samly POS shortcut appears on the desktop.",
      },
      connect: {
        title: "Connect the POS",
        text: "Open Samly POS and enter the connection code. You find the code in the back office: Branch settings → Devices.",
      },
    },
    retry: "Download did not start?",
    retryLink: "Download again",
    help: "Still stuck? Message us on Telegram",
    close: "Got it",
  },
};
